<template>
  <TransitionRoot appear :show="props.modelValue" as="template">
    <Dialog as="div" @close="closeModal" class="relative z-[9999999]">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-untitled-gray-500 bg-opacity-40 backdrop-blur-sm"
        />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div
          class="flex min-h-full items-start justify-center p-4 pt-[10vh] text-center"
        >
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-md transform rounded-xl bg-white text-left align-middle shadow-2xl transition-all overflow-hidden flex flex-col max-h-[70vh] relative"
            >
              <div
                v-if="impersonatingId !== null"
                class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/90 backdrop-blur-sm"
              >
                <div
                  class="h-6 w-6 rounded-full border-2 border-untitled-gray-200 border-t-untitled-gray-500 animate-spin"
                />
                <p class="text-sm font-medium text-untitled-gray-700">
                  Signing in as {{ impersonatingUserName }}&hellip;
                </p>
              </div>

              <div
                class="flex items-center justify-between gap-3 px-4 py-3 border-b border-untitled-gray-100 shrink-0"
              >
                <div>
                  <h2 class="text-sm font-semibold text-untitled-gray-900">
                    Switch User
                  </h2>
                  <p class="text-xs text-untitled-gray-500">
                    You'll be signed in as the selected user
                  </p>
                </div>
                <button
                  type="button"
                  class="shrink-0 text-untitled-gray-400 hover:text-untitled-gray-600"
                  @click="closeModal"
                >
                  <XIcon class="h-4 w-4" />
                </button>
              </div>

              <div
                class="flex items-center gap-3 px-4 border-b border-untitled-gray-100 shrink-0"
              >
                <SearchIcon class="h-4 w-4 text-untitled-gray-400 shrink-0" />
                <input
                  v-model="search"
                  @input="handleInput"
                  @keypress.enter="submitSearch"
                  class="flex-1 border-0 py-3 text-sm text-untitled-gray-900 placeholder-untitled-gray-400 focus:outline-none focus:ring-0"
                  type="text"
                  placeholder="Search by name or email..."
                  autofocus
                />
                <button
                  v-if="search?.length"
                  type="button"
                  class="text-untitled-gray-400 hover:text-untitled-gray-600 shrink-0"
                  @click="clearSearch"
                >
                  <XIcon class="h-4 w-4" />
                </button>
              </div>

              <div class="overflow-y-auto px-2 py-2 flex-1">
                <div v-if="isLoading" class="space-y-1 p-2">
                  <div
                    v-for="n in 2"
                    :key="n"
                    class="flex items-start justify-start gap-3 px-2 py-2"
                  >
                    <div class="size-16 bg-untitled-gray-100 animate-pulse rounded-full" />
                    <div class="flex-1 space-y-1.5">
                      <ElSkeleton :rows="1" animated />
                    </div>
                  </div>
                </div>

                <template v-else-if="isSearching && search?.length">
                  <ul v-if="users.length" class="space-y-0.5">
                    <li v-for="u in users.slice(0, 5)" :key="u.id">
                      <button
                        type="button"
                        :disabled="impersonatingId !== null"
                        class="group flex items-center gap-3 w-full rounded-lg px-2 py-2 text-left hover:bg-untitled-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        @click="impersonate(u.id)"
                      >
                        <div
                          class="flex items-center justify-center w-8 h-8 rounded-full bg-untitled-gray-100 text-xs font-medium text-untitled-gray-600 shrink-0"
                        >
                          {{ getInitials(u.name) }}
                        </div>
                        <div class="min-w-0 flex-1">
                          <p
                            class="text-sm font-medium text-untitled-gray-900 truncate"
                          >
                            {{ u.name }}
                          </p>
                          <p class="text-xs text-untitled-gray-500 truncate">
                            {{ u.email }}
                          </p>
                        </div>
                        <div
                          v-if="impersonatingId === u.id"
                          class="h-4 w-4 shrink-0 rounded-full border-2 border-untitled-gray-200 border-t-untitled-gray-400 animate-spin"
                        />
                        <UserIcon
                          v-else
                          class="h-4 w-4 shrink-0 text-untitled-gray-300 opacity-0 group-hover:opacity-100 transition-opacity"
                        />
                      </button>
                    </li>
                    <li
                      v-if="users.length > 5"
                      class="px-2 pt-1 text-xs font-medium text-untitled-gray-400"
                    >
                      Keep typing to narrow results
                    </li>
                  </ul>

                  <div
                    v-else
                    class="flex flex-col items-center justify-center gap-1 py-14 text-untitled-gray-400"
                  >
                    <UserIcon class="h-8 w-8 mb-1" />
                    <p class="text-sm font-medium text-untitled-gray-500">
                      No users found for &ldquo;{{ search }}&rdquo;
                    </p>
                    <p class="text-xs">Try a different name or email.</p>
                  </div>
                </template>

                <div
                  v-else
                  class="flex items-center justify-center py-14 text-sm text-untitled-gray-400"
                >
                  Start typing a name or email to find a user
                </div>
              </div>

              <p
                v-if="errorMessage"
                class="px-4 py-2 text-xs text-red-600 border-t border-untitled-gray-100 shrink-0"
              >
                {{ errorMessage }}
              </p>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
<script setup>
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
} from "@headlessui/vue";
import { ElSkeleton } from "element-plus";
import { SearchIcon, UserIcon, XIcon } from "@heroicons/vue/outline";
import { computed, ref, defineProps, defineEmits } from "vue";
import { useCookies } from "vue3-cookies";
import { useAuth } from "@websanova/vue-auth/src/v3.js";
import { useAuthStore } from "@/stores";
import { debounce as _debounce } from "lodash-es";
import Bugsnag from "@bugsnag/js";
import { http } from "@/composable/httpService";

const emit = defineEmits(["update:modelValue"]);

const props = defineProps({
  modelValue: {
    default: false,
    type: Boolean,
  },
});

const { cookies } = useCookies();
const auth = useAuth();
const authStore = useAuthStore();
const search = ref("");
const isSearching = ref(false);
const isLoading = ref(false);
const users = ref([]);
const impersonatingId = ref(null);
const errorMessage = ref("");

const impersonatingUserName = computed(
  () => users.value.find((u) => u.id === impersonatingId.value)?.name
);

const getInitials = (name) =>
  name
    ?.split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() ?? "";

const closeModal = () => {
  globalSearch.cancel();
  emit("update:modelValue", false);
  search.value = "";
  users.value = [];
  isSearching.value = false;
  isLoading.value = false;
  errorMessage.value = "";
};

const clearSearch = () => {
  globalSearch.cancel();
  search.value = "";
  users.value = [];
  isSearching.value = false;
  isLoading.value = false;
};

const impersonate = (id) => {
  if (impersonatingId.value !== null) return;

  impersonatingId.value = id;
  errorMessage.value = "";

  http()
    .post(`impersonate-user/${id}`)
    .then(({ data }) => {
      localStorage.setItem("impersonate_user", JSON.stringify(data.user));
      localStorage.setItem("impersonate_token", JSON.stringify(data.token));

      cookies.set("default_auth_token", data.token);
      cookies.set("default_auth_user", data.user.id);
      auth.user(data.user);
      auth.token(null, data.token, false);
      authStore.$patch({
        user: data.user,
        token: data.token,
      });

      window.location.reload();
    })
    .catch((error) => {
      impersonatingId.value = null;
      errorMessage.value = "Unable to switch user. Please try again.";
      Bugsnag.notify(error);
    });
};

const globalSearch = _debounce(() => {
  if (!search.value?.length) return;
  isLoading.value = true;
  http()
    .get("search-user", {
      params: {
        search_query: search.value,
      },
    })
    .then(({ data }) => {
      users.value = Object.values(data);
      isLoading.value = false;
    })
    .catch((error) => {
      isLoading.value = false;
      users.value = [];
      Bugsnag.notify(error);
    });
}, 400);

const handleInput = () => {
  if (!search.value?.length) {
    isSearching.value = false;
    isLoading.value = false;
    users.value = [];
    globalSearch.cancel();
    return;
  }

  isSearching.value = true;
  isLoading.value = true;
  globalSearch();
};

const submitSearch = () => {
  if (!search.value?.length) return;

  isLoading.value = true;
  globalSearch.flush();
};
</script>
